import {
    AutocompleteApi,
    AutocompleteState,
    BaseItem
} from "@algolia/autocomplete-core";
import { Button } from "@omni/ui/components/Button/button.component";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import {
    BaseSyntheticEvent,
    ChangeEvent,
    FormEvent,
    ForwardedRef,
    forwardRef,
    KeyboardEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef
} from "react";

import { Col } from "@/components/General/Grid/col.component";
import { AutocompleteRef } from "@/components/Search/SearchBar/searchBar.types";
import { isValidQuery } from "@/redux/modules/value/search/helpers/search.helpers";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { preventUntrusted } from "@/utils/domElements";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

import { getLastAutocompleteItem } from "../../helpers/accessibility.helpers";
import { AutocompleteActions } from "../../useAutocompleteApi.hook";
import focusElementByRef from "./focus.helper";

type AutocompleteSearchBoxComponentProps = {
    isOpen: boolean;
    autocomplete: AutocompleteApi<
        any,
        BaseSyntheticEvent,
        MouseEvent,
        KeyboardEvent
    >;
    autocompleteState?: AutocompleteState<BaseItem>;
    open: () => void;
    close: () => void;
    autocompleteActions?: AutocompleteActions;
};

const SearchBoxComponent = (
    {
        isOpen,
        autocomplete,
        autocompleteState,
        open,
        close,
        autocompleteActions
    }: AutocompleteSearchBoxComponentProps,
    ref: ForwardedRef<AutocompleteRef>
) => {
    const { t } = useGranite();

    const inputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const resetButtonRef = useRef<HTMLButtonElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const queryRef = useRef<string | null>(null);

    const props = {
        form: autocomplete.getFormProps({
            inputElement: inputRef.current
        }),
        input: autocomplete.getInputProps({
            inputElement: inputRef.current
        })
    };

    const forceFocusOnInputRef = useCallback(() => {
        const timeout = focusElementByRef(inputRef);
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, []);

    const handleOnInputKeyDown = createKeyHandler<KeyboardEvent>([
        {
            keys: [KeyNames.ENTER],
            handler: (event) => {
                const activeItemId = autocompleteState?.activeItemId;
                if (isDefined(activeItemId)) return;

                event.stopPropagation();

                if (!isValidQuery(inputRef.current?.value)) {
                    event.preventDefault();
                } else {
                    props.input.onKeyDown(event);
                }
            }
        },
        {
            keys: [KeyNames.TAB],
            handler: (event) => {
                if (!event.shiftKey) return;

                const lastAutocompleteItem = getLastAutocompleteItem();

                if (!isDefined(lastAutocompleteItem)) return;

                event.preventDefault();
                lastAutocompleteItem.focus();
            }
        },
        {
            defaultExcept: [KeyNames.TAB],
            handler: (event) => props.input.onKeyDown(event)
        }
    ]);

    const handleOnSubmit = (event: FormEvent) => {
        if (!isValidQuery(autocompleteState?.query) || !event.isTrusted) {
            event.preventDefault();
        } else {
            props.form.onSubmit(event);
        }
    };

    const handleOnResetClick = (event: MouseEvent) => {
        event.preventDefault();

        autocompleteState?.query && autocomplete.setQuery("");
        autocompleteActions?.setQuery("");

        open();
        forceFocusOnInputRef();
    };

    const handleOnCloseClick = (event: MouseEvent) => {
        event.stopPropagation();
        close();
    };

    const handleOnInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        queryRef.current = event.target.value;
        props.input.onChange(event);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isOpen) {
            forceFocusOnInputRef();
        }
    }, [isOpen]);

    useImperativeHandle(ref, () => ({
        get input() {
            return inputRef.current;
        },
        get form() {
            return formRef.current;
        },
        get query() {
            return queryRef.current;
        },
        set query(query: string | null) {
            queryRef.current = query;
        }
    }));

    return (
        <div className={cn("tw:flex tw:items-center")}>
            {isOpen && (
                <button
                    type={"button"}
                    className={cn(
                        "tw:bg-white tw:text-primary tw:border-none tw:min-h-10 tw:w-10 tw:mr-4 tw:transition-colors tw:flex tw:items-center tw:justify-center tw:rounded-sm",
                        "tw:lg:hidden",
                        "tw:hover:bg-grey",
                        "tw:active:bg-grey tw:active:border tw:active:border-primary",
                        "tw:focus:bg-grey tw:focus:border tw:focus:border-primary"
                    )}
                    onClick={handleOnCloseClick}
                    tabIndex={isOpen ? 0 : -1}
                    aria-label={t("accessibility.searchBox.close")}
                >
                    <i className={cn("las la-lg la-arrow-left")} />
                </button>
            )}
            <form
                {...props.form}
                className={cn(
                    "tw:h-10 tw:bg-primary tw:layer-modal/[1] tw:rounded-sm tw:border tw:border-primary tw:relative tw:flex tw:flex-wrap tw:mx-0 tw:overflow-hidden tw:grow",
                    {
                        // TODO - Styling
                        "searchBox--active": isOpen
                    }
                )}
                ref={formRef}
                onSubmit={handleOnSubmit}
            >
                <Col
                    xs={9}
                    sm={10}
                    md={11}
                    lg={10}
                    className={cn(
                        "tw:min-w-0 tw:transition-shadow tw:bg-grey tw:ring-inset tw:ring-primary/30 tw:flex tw:p-0",
                        "tw:group-focus-within/autocomplete:ring-3!"
                    )}
                >
                    <button
                        className={cn(
                            "tw:border-none tw:hidden tw:pl-3",
                            "tw:lg:block"
                        )}
                        type="button"
                        onClick={open}
                        tabIndex={-1}
                        aria-label={t("accessibility.searchBox.open")}
                    >
                        <i className={cn("las la-lg la-search")}></i>
                    </button>
                    <input
                        id={"autocomplete-input"}
                        className={cn(
                            "tw:grow tw:border-none tw:pl-4 tw:lg:pl-2  tw:outline-none! tw:rounded-none tw:min-w-0 tw:[&::-webkit-search-cancel-button]:appearance-none",
                            "tw:lg:pl-2"
                        )}
                        ref={inputRef}
                        type={props.input.type}
                        value={props.input.value ?? ""}
                        tabIndex={0}
                        autoComplete={props.input.autoComplete}
                        autoCapitalize={"none"}
                        autoCorrect={props.input.autoCorrect}
                        enterKeyHint={props.input.enterKeyHint}
                        maxLength={props.input.maxLength}
                        placeholder={props.input.placeholder}
                        spellCheck={props.input.spellCheck}
                        onBlur={props.input.onBlur}
                        onChange={handleOnInputChange}
                        onKeyDown={handleOnInputKeyDown}
                        onClick={() => {
                            forceFocusOnInputRef();
                            props.input.onClick;
                        }}
                        onCompositionEnd={props.input.onCompositionEnd}
                        onFocus={props.input.onFocus}
                        aria-controls={props.input["aria-controls"]}
                        aria-autocomplete={props.input["aria-autocomplete"]}
                        aria-activedescendant={
                            props.input["aria-activedescendant"]
                        }
                        aria-label={t("accessibility.searchBox.label")}
                    />
                    {autocompleteState?.query && (
                        <button
                            className={cn(
                                "tw:border-none tw:px-4 tw:capitalize tw:text-grey-input",
                                "tw:hover:underline",
                                "tw:focus:underline"
                            )}
                            ref={resetButtonRef}
                            type={"reset"}
                            onClick={handleOnResetClick}
                            tabIndex={isOpen ? 0 : -1}
                            aria-label={t("searchBox.input.button.reset")}
                        >
                            <span className={cn("tw:hidden tw:lg:block")}>
                                {t("searchBox.input.button.reset")}
                            </span>
                            <i
                                className={cn(
                                    "las la-lg la-times",
                                    "tw:block tw:lg:hidden"
                                )}
                            />
                        </button>
                    )}
                </Col>
                <Col
                    xs={3}
                    sm={2}
                    md={1}
                    lg={2}
                    className={cn("tw:p-0")}
                >
                    <Button
                        color={"primary"}
                        onClick={preventUntrusted()}
                        fullWidth={true}
                        type={"submit"}
                        tabIndex={0}
                        ref={submitButtonRef}
                        aria-label={t("searchBox.input.button.submit")}
                        className={cn("tw:h-full tw:w-full")}
                    >
                        <span
                            id={"submit-search-button"}
                            className={cn("tw:hidden tw:lg:block")}
                        >
                            {t("searchBox.input.button.submit")}
                        </span>
                        <i
                            className={cn(
                                "las la-lg la-search",
                                "tw:block tw:lg:hidden"
                            )}
                        />
                    </Button>
                </Col>
            </form>
        </div>
    );
};

export default forwardRef(SearchBoxComponent);
